import React, { useEffect, useState } from "react";
import { AlertTriangle, Ban, Loader2, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, errMsg, fmtDate } from "../lib/api";
import { pokeLiveCollections } from "../lib/liveCollections";

export const MIN_REASON = 5;
export const RESTORE_WINDOW_DAYS = 30;
export const BLOCKED_RECEIPTS_MSG = "Delete the receipts first";

/**
 * Phase 192 — "Delete this property?" confirmation with the MANDATORY reason.
 *   property    one row {id, pin, owner_name, ward_label?, collection_summary?} (single delete)
 *   properties  several rows (bulk delete — one reason for all; rows with receipts are skipped by the server)
 *   code        tenant code the property belongs to (the Super Admin page passes the picked municipality)
 * The dialog pre-checks a single property (`/properties/delete/check/{id}`) and shows the "Delete the receipts first"
 * block in place when receipts exist, instead of letting the user type a reason for nothing.
 */
export const DeletePropertyDialog = ({ open, onOpenChange, property, properties, code, onDeleted, testId = "delete-property" }) => {
  const bulk = Array.isArray(properties) && properties.length > 0;
  const rows = bulk ? properties : property ? [property] : [];
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState(null);     // single: {can_delete, blocked_reason, payments_count, demand_count}
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason(""); setBusy(false); setCheck(null);
    if (!bulk && property?.id) {
      setChecking(true);
      api.get(`/t/${code}/properties/delete/check/${property.id}`)
        .then((r) => setCheck(r.data))
        .catch((e) => setCheck({ can_delete: true, error: errMsg(e) }))
        .finally(() => setChecking(false));
    }
  }, [open, bulk, property?.id, code]);

  const trimmed = reason.trim();
  const valid = trimmed.length >= MIN_REASON;
  const blocked = !bulk && check && check.can_delete === false;
  const preBlocked = bulk ? rows.filter((r) => Number(r.collection_summary?.payments_count || r.payments_count || 0) > 0) : [];

  const confirm = async () => {
    if (!valid || busy || blocked || rows.length === 0) return;
    setBusy(true);
    try {
      if (bulk) {
        const r = await api.post(`/t/${code}/properties/delete`, { property_ids: rows.map((p) => p.id), reason: trimmed });
        const d = r.data;
        if (d.deleted > 0) {
          toast.success(`${d.deleted} propert${d.deleted === 1 ? "y" : "ies"} deleted`, {
            description: `Hidden from every view. The Super Admin can restore them from Deleted properties until ${fmtDate(d.restore_until)}.`
              + (d.blocked ? ` ${d.blocked} skipped — ${BLOCKED_RECEIPTS_MSG}.` : ""),
          });
        }
        if (d.blocked > 0 && d.deleted === 0) {
          toast.error(`Nothing deleted — ${BLOCKED_RECEIPTS_MSG}`, {
            description: `${d.blocked} selected propert${d.blocked === 1 ? "y has" : "ies have"} receipts: ${(d.blocked_items || []).slice(0, 5).map((b) => b.pin).join(", ")}${d.blocked > 5 ? "…" : ""}`,
          });
        }
        pokeLiveCollections();
        onOpenChange(false);
        onDeleted?.(d);
      } else {
        const r = await api.post(`/t/${code}/properties/delete/${property.id}`, { reason: trimmed });
        toast.success(`Property ${r.data.pin} deleted`, {
          description: `Hidden from every view. The Super Admin can restore it from Deleted properties until ${fmtDate(r.data.restore_until)}.`,
        });
        pokeLiveCollections();
        onOpenChange(false);
        onDeleted?.(r.data);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const title = bulk ? `Delete ${rows.length} selected propert${rows.length === 1 ? "y" : "ies"}?` : `Delete property ${property?.pin || ""}?`;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-lg" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Trash2 className="h-5 w-5 text-destructive" /> {title}
          </DialogTitle>
          <DialogDescription>
            The property, its demands and assignments disappear from every register, report and dashboard immediately. It stays in
            <span className="font-medium"> Deleted properties</span> for {RESTORE_WINDOW_DAYS} days, where <span className="font-medium">only the Super Admin</span> can
            restore it; after {RESTORE_WINDOW_DAYS} days the Super Admin can remove it permanently. Properties that have receipts can never be deleted.
          </DialogDescription>
        </DialogHeader>

        {!bulk && property && (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm grid grid-cols-2 gap-x-4 gap-y-1" data-testid={`${testId}-summary`}>
            <span className="text-muted-foreground">PIN</span><span className="font-mono text-right" data-testid={`${testId}-pin`}>{property.pin || "—"}</span>
            <span className="text-muted-foreground">Owner</span><span className="text-right truncate">{property.owner_name || "—"}</span>
            {(property.ward_label || property.zone_label) && (<><span className="text-muted-foreground">Area</span><span className="text-right truncate">{[property.zone_label, property.ward_label, property.mohalla_label].filter(Boolean).join(" • ")}</span></>)}
            <span className="text-muted-foreground">Demands</span>
            <span className="text-right tabular-nums">{checking ? "…" : check?.demand_count ?? "—"}</span>
            <span className="text-muted-foreground">Receipts</span>
            <span className={`text-right tabular-nums ${check?.payments_count ? "text-destructive font-medium" : ""}`} data-testid={`${testId}-receipts`}>{checking ? "…" : check?.payments_count ?? "—"}</span>
            {property.municipality?.name && (<><span className="text-muted-foreground">Municipality</span><span className="text-right truncate">{property.municipality.name}</span></>)}
          </div>
        )}

        {bulk && (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm space-y-1.5" data-testid={`${testId}-bulk-summary`}>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Selected</span>
              <Badge variant="secondary" className="tabular-nums" data-testid={`${testId}-bulk-count`}>{rows.length}</Badge>
            </div>
            <p className="font-mono text-xs text-muted-foreground break-words" data-testid={`${testId}-bulk-pins`}>
              {rows.slice(0, 12).map((p) => p.pin).join(", ")}{rows.length > 12 ? ` … +${rows.length - 12} more` : ""}
            </p>
            {preBlocked.length > 0 && (
              <p className="text-xs text-[hsl(var(--warning))] flex items-start gap-1.5" data-testid={`${testId}-bulk-preblocked`}>
                <Receipt className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{preBlocked.length} of these ha{preBlocked.length === 1 ? "s" : "ve"} receipts and will be skipped — {BLOCKED_RECEIPTS_MSG}.</span>
              </p>
            )}
          </div>
        )}

        {blocked ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm" data-testid={`${testId}-blocked`}>
            <Ban className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
            <div>
              <p className="font-medium text-destructive">{BLOCKED_RECEIPTS_MSG}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{check.blocked_reason || "This property has receipts and cannot be deleted while any receipt exists."} Delete those receipts from Collections → Receipts, then try again.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label htmlFor={`${testId}-reason`}>Reason for deletion <span className="text-destructive">*</span></Label>
              <Textarea id={`${testId}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500}
                placeholder={bulk ? "e.g. Test rows created during the import trial; duplicate entries of ward 4" : "e.g. Duplicate of PIN …; property demolished; entered against the wrong ward"}
                aria-invalid={trimmed.length > 0 && !valid} data-testid={`${testId}-reason-input`} />
              <p className={`text-[11px] ${trimmed.length > 0 && !valid ? "text-destructive" : "text-muted-foreground"}`} data-testid={`${testId}-reason-hint`}>
                Mandatory — at least {MIN_REASON} characters. Your name, the time and this reason are logged in the Audit Trail{bulk ? " for every property" : ""}.
              </p>
            </div>
            <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs text-foreground" data-testid={`${testId}-warning`}>
              <AlertTriangle className="h-4 w-4 shrink-0 text-[hsl(var(--warning))] mt-0.5" />
              <span>Open demands move to the recycle bin with the property and come back on restore. You will not be able to undo this yourself — only the Super Admin can restore.</span>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${testId}-cancel`}>{blocked ? "Close" : "Cancel"}</Button>
          {!blocked && (
            <Button variant="destructive" onClick={confirm} disabled={!valid || busy || checking || rows.length === 0} data-testid={`${testId}-confirm`}>
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              {busy ? "Deleting…" : bulk ? `Delete ${rows.length} propert${rows.length === 1 ? "y" : "ies"}` : "Delete property"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeletePropertyDialog;
